import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import Icon from '../../components/AppIcon';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useToast } from '../../context/ToastContext';
import {
  fetchSupportThreads,
  fetchSupportThreadWithCustomer,
  fetchSupportMessages,
  sendSupportReply,
  resolveSupportThread,
  subscribeToSupportUpdates,
} from '../../lib/adminApi';

export default function SupportPage() {
  const { profile } = useAdminAuth();
  const toast = useToast();
  const [filter, setFilter] = useState('open'); // 'open' | 'resolved' | 'all'
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadDetail, setThreadDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);
  const [resolving, setResolving] = useState(false);

  const loadThreads = async () => {
    setLoading(true);
    setError(null);
    try {
      const resolved = filter === 'resolved' ? true : filter === 'all' ? undefined : false;
      const data = await fetchSupportThreads({ resolved });
      setThreads(data);
    } catch (err) {
      setError(err?.message ?? 'Failed to load threads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadThreads();
  }, [filter]);

  // Realtime: when a customer creates a thread or sends a message, refresh list and show notification
  useEffect(() => {
    const unsubscribe = subscribeToSupportUpdates(() => {
      loadThreads();
      toast.success('New support message from customer');
    });
    return unsubscribe;
  }, []);

  const openThread = async (thread) => {
    setSelectedThread(thread);
    setThreadDetail(null);
    setMessages([]);
    setReplyBody('');
    try {
      const [detail, msgs] = await Promise.all([
        fetchSupportThreadWithCustomer(thread.id),
        fetchSupportMessages(thread.id),
      ]);
      setThreadDetail(detail);
      setMessages(msgs);
    } catch (err) {
      toast.error(err?.message ?? 'Failed to load thread');
    }
  };

  const handleSendReply = async () => {
    if (!selectedThread?.id || !replyBody.trim() || !profile?.user_id) return;
    setSending(true);
    try {
      await sendSupportReply(selectedThread.id, replyBody.trim(), profile.user_id);
      setReplyBody('');
      const msgs = await fetchSupportMessages(selectedThread.id);
      setMessages(msgs);
      toast.success('Reply sent');
    } catch (err) {
      toast.error(err?.message ?? 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const handleMarkResolved = async () => {
    if (!selectedThread?.id) return;
    setResolving(true);
    try {
      await resolveSupportThread(selectedThread.id);
      toast.success('Thread marked resolved');
      setSelectedThread(null);
      setThreadDetail(null);
      setMessages([]);
      loadThreads();
    } catch (err) {
      toast.error(err?.message ?? 'Failed to resolve');
    } finally {
      setResolving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Customer support</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Reply to customer inquiries and mark threads resolved. Resolved threads disappear from the customer&apos;s order page.
        </p>
      </div>

      <div className="flex gap-2">
        {['open', 'resolved', 'all'].map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`rounded-md border px-3 py-2 text-sm font-medium ${
              filter === f ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card text-foreground hover:bg-muted'
            }`}
          >
            {f === 'open' ? 'Open' : f === 'resolved' ? 'Resolved' : 'All'}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-card">
          <div className="border-b border-border px-4 py-3">
            <h2 className="font-semibold text-foreground">Threads</h2>
          </div>
          <div className="max-h-[480px] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <span className="text-sm text-muted-foreground">Loading…</span>
              </div>
            ) : threads.length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground">No threads</div>
            ) : (
              <ul className="divide-y divide-border">
                {threads.map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => openThread(t)}
                      className={`flex w-full items-center justify-between px-4 py-3 text-left hover:bg-muted/50 ${
                        selectedThread?.id === t.id ? 'bg-primary/10' : ''
                      }`}
                    >
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          Order: {t.order_id ? String(t.order_id).slice(0, 8) + '…' : '—'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t.created_at ? format(new Date(t.created_at), 'dd MMM yyyy HH:mm') : ''}
                          {t.resolved_at ? ' · Resolved' : ''}
                        </p>
                      </div>
                      <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card">
          <div className="border-b border-border px-4 py-3 flex items-center justify-between">
            <h2 className="font-semibold text-foreground">
              {threadDetail ? threadDetail.customer_name || 'Customer' : 'Select a thread'}
            </h2>
            {threadDetail && !threadDetail.resolved_at && (
              <button
                type="button"
                onClick={handleMarkResolved}
                disabled={resolving}
                className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-60"
              >
                {resolving ? 'Resolving…' : 'Mark resolved'}
              </button>
            )}
          </div>
          <div className="flex flex-col max-h-[520px]">
            <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[200px]">
              {!selectedThread && (
                <p className="text-sm text-muted-foreground">Select a thread from the list to view messages and reply.</p>
              )}
              {selectedThread && messages.length === 0 && !threadDetail?.resolved_at && (
                <p className="text-sm text-muted-foreground">No messages yet.</p>
              )}
              {messages.map((m) => (
                <div
                  key={m.id}
                  className={`rounded-lg p-3 max-w-[85%] ${m.sender_type === 'admin' ? 'ml-auto bg-primary/15' : 'bg-muted'}`}
                >
                  <p className="text-xs font-medium text-muted-foreground">{m.sender_type === 'customer' ? 'Customer' : 'Support'}</p>
                  <p className="text-sm text-foreground mt-0.5">{m.body}</p>
                  <p className="text-xs text-muted-foreground mt-1">{m.created_at ? format(new Date(m.created_at), 'dd MMM HH:mm') : ''}</p>
                </div>
              ))}
            </div>
            {selectedThread && threadDetail && !threadDetail.resolved_at && (
              <div className="border-t border-border p-4">
                <textarea
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  placeholder="Type your reply…"
                  rows={3}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
                />
                <div className="mt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={handleSendReply}
                    disabled={sending || !replyBody.trim()}
                    className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
                  >
                    {sending ? 'Sending…' : 'Send reply'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
